import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { select } from '@ngxs/store';
import {
  Currency,
  SUBSCRIPTION_CHECKOUT_DATA,
  SUBSCRIPTION_LIMITATIONS,
  SubscriptionPeriod,
  SubscriptionType,
} from '../../../../../common/dto/subscription.dto';
import { FeatureLimitationKey } from '../../../../../common/model/feature-permissions.enum';
import { formatBigNumber } from '../../../../../common/utils/number.utils';
import { getSubscriptionFeatures } from '../../../../../common/utils/subscription.utils';
import { AppState } from '../../app.state';
import { PillComponent } from '../components/pill/pill.component';
import { BigNumberPipe } from '../pipes/big-number.pipe';
import { RouteNavigationService } from '../routing/route-navigation.service';
import { AuthenticationService } from '../services/authentications.service';
import { LemonSqueezyService } from '../services/lemon-squeezy.service';
import { PricingSubComponent } from './pricing-sub/pricing-sub.component';

@Component({
  selector: 'app-pricing',
  imports: [
    PricingSubComponent,
    BigNumberPipe,
    MatSliderModule,
    FormsModule,
    MatSlideToggle,
    PillComponent,
    CurrencyPipe,
    MatSelectModule,
  ],
  templateUrl: './pricing.component.html',
  styleUrl: './pricing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingComponent {
  private _baseSubsInOrder = Object.entries(SUBSCRIPTION_LIMITATIONS)
    .filter(_ => _[0] !== SubscriptionType.Free && _[0] !== SubscriptionType.Enterprise)
    .map(_ => {
      return {
        type: <SubscriptionType>_[0],
        mau: _[1][FeatureLimitationKey.MonthlyActiveUsers],
      };
    })
    .sort((a, b) => a.mau - b.mau);

  private _userSession = select(AppState.session);

  constructor(
    private _lemonSqueezyService: LemonSqueezyService,
    private _authenticationService: AuthenticationService,
    private _routeNavigationService: RouteNavigationService,
  ) {
  }

  public subscriptionsChanged = output();

  protected currentMAUIndex = model<number>(1);
  protected currentMAUValue = computed(() => this.steps[this.currentMAUIndex()]);
  protected currency = signal<Currency>(Currency.EUR);
  protected period = signal<SubscriptionPeriod>(SubscriptionPeriod.Annual);

  protected currentBaseSubscription = computed(() => {
    const mau = this.currentMAUValue();

    return this._baseSubsInOrder.find(_ => _.mau >= mau);
  });

  protected currentBaseSubscriptionFeatures = computed(() => getSubscriptionFeatures(this.currentBaseSubscription()?.type ?? SubscriptionType.Free));

  protected saveValue = computed<number>(() => {
    const currentBaseSub = this.currentBaseSubscription();

    if (!currentBaseSub) {
      return 0;
    }

    const data = SUBSCRIPTION_CHECKOUT_DATA[currentBaseSub.type];

    return (12 * data.monthly.price[this.currency()]!) - data.annual.price[this.currency()]!;
  });

  protected steps = [
    0,
    500,
    1000,
    3000,
    5000,
    10000,
    20000,
  ];

  protected async startSubscriptionAsync(subscriptionType: SubscriptionType | undefined) {
    const currentSubs = new Set(this._userSession().company!.subscriptions.map(_ => _.type));

    await this._lemonSqueezyService.startCheckoutAsync(subscriptionType ?? SubscriptionType.Base_1k, this.period(), this._userSession().company!.companyId);

    await this._authenticationService.refreshUserSessionAsync();

    const newSubs = new Set(this._userSession().company!.subscriptions.map(_ => _.type));

    if (currentSubs.difference(newSubs).size) {
      await this._routeNavigationService.goToBillingAsync();

      this.subscriptionsChanged.emit();
    }
  }

  protected async togglePeriod() {
    this.period.set(this.period() === SubscriptionPeriod.Monthly ? SubscriptionPeriod.Annual : SubscriptionPeriod.Monthly);
  }

  protected formatLabel = (value: number) => {
    const formatted = formatBigNumber(this.steps[value]);

    if (value === (this.steps.length - 1)) {
      return formatted + '+';
    }

    return formatted;
  };

  protected readonly FREE_FEATURES = getSubscriptionFeatures(SubscriptionType.Free);
  protected readonly ENTERPRISE_FEATURES = getSubscriptionFeatures(SubscriptionType.Enterprise);

  protected readonly SUBSCRIPTION_CHECKOUT_DATA = SUBSCRIPTION_CHECKOUT_DATA;

  protected readonly SubscriptionType = SubscriptionType;

  protected readonly SubscriptionPeriod = SubscriptionPeriod;
}
